import React, { useEffect, useReducer, useRef } from 'react';

import { set } from '../utils';

const LOADING_START = 'LOADING_START';
const SET_DATA = 'SET_DATA';
const SET_ERROR = 'SET_ERROR';

type HierarchyAction = {
  type: string;
  payload: { key: string; data?: any; error?: any };
};

interface HierarchyState {
  [field: string]: {
    key: string;
    data?: any;
    error?: null;
    isLoading: boolean;
  };
}

function hierarchyReducer(state: HierarchyState, action: HierarchyAction) {
  const { type, payload } = action;
  const { key, data, error } = payload;
  switch (type) {
    case LOADING_START:
      return set(state, `${key}`, {
        isLoading: true,
        data: null,
        error: null,
      });
    case SET_DATA:
      return set(state, `${key}`, {
        isLoading: false,
        data: data,
        error: null,
      });
    case SET_ERROR:
      return set(state, `${key}`, {
        isLoading: false,
        data: null,
        error: error,
      });

    default:
      return state;
  }
}

export interface Values {
  [field: string]: any;
}

interface HierarchyConfig {
  values: Values;
}
interface AreaRegistry {
  [field: string]: {
    count: number;
    area: any;
    paramsKey: string | null;
  };
}

export const useHierarchy = ({ values }: HierarchyConfig) => {
  const areaRegistry = useRef<AreaRegistry>({});
  const [state, dispatch] = useReducer<
    React.Reducer<HierarchyState, HierarchyAction>
  >(hierarchyReducer, {});

  const fetchOne = async (key: string, source: any, params: any) => {
    let areaData = null;
    let areaError = null;

    // areaRegistry.current[key].paramsKey = JSON.stringify(params);

    dispatch({
      type: LOADING_START,
      payload: { key },
    });

    try {
      areaData = await source(params);
      dispatch({
        type: SET_DATA,
        payload: { key, data: areaData },
      });
    } catch (e) {
      areaError = e;
      dispatch({
        type: SET_ERROR,
        payload: { key, error: areaError },
      });
      throw e;
    }

    return areaData;
  };

  const registerArea = (key: string, area: any) => {
    if (!areaRegistry.current[key]) {
      areaRegistry.current[key] = {
        area,
        count: 1,
        paramsKey: null,
      };
      fetchIfNeeded(key, values);
    } else {
      areaRegistry.current[key].count++;
    }
  };

  const unregisterArea = (key: string) => {
    if (!--areaRegistry.current[key].count) {
      delete areaRegistry.current[key];
    }
  };

  const fetchIfNeeded = async (key: string, values: Values) => {
    const { area } = areaRegistry.current[key];

    const paramsObj = area.params(values, area.depends);
    const paramsKey = JSON.stringify(paramsObj);

    if (areaRegistry.current[key].paramsKey !== paramsKey) {
      try {
        fetchOne(key, area.source, paramsObj);
      } catch (e) {
        // throw e;
      } finally {
        areaRegistry.current[key].paramsKey = paramsKey;
      }
    }
  };

  useEffect(() => {
    Object.keys(areaRegistry.current).forEach(key => {
      fetchIfNeeded(key, values);
    });
  }, [values]);

  return {
    state: state,
    register: registerArea,
    unregister: unregisterArea,
  };
};
